import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../common/Modal';
import ModalContent from '../common/ModalContent';
import ModalCloseButton from '../common/ModalCloseButton';
import { Button } from '../common';
import { Heading, Text } from '../common/Typography';

const SEARCH_INPUT_CLASSES =
  'w-full px-4 py-2.5 pl-10 border border-border-medium rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent bg-surface text-text-primary text-sm placeholder:text-text-muted';

const SECTION_LABEL_CLASSES =
  'text-[0.7rem] font-bold uppercase tracking-wider text-text-muted mb-2';

function matchesQuery(club, q) {
  if (!q.trim()) return true;
  const s = q.trim().toLowerCase();
  const name = String(club.name || '').toLowerCase();
  const loc = club.location ? String(club.location).toLowerCase() : '';
  return name.includes(s) || loc.includes(s);
}

function sortPerformanceClubs(rows) {
  return [...rows].sort((a, b) => {
    const tierA = ['owner', 'admin', 'host'].includes(a.club_role) ? 0 : 1;
    const tierB = ['owner', 'admin', 'host'].includes(b.club_role) ? 0 : 1;
    if (tierA !== tierB) return tierA - tierB;
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
      sensitivity: 'base'
    });
  });
}

/**
 * @typedef {{ id: string, name: string, location?: string|null, club_role: string }} PerformanceClubRow
 * @param {{ isOpen: boolean, onClose: () => void, clubs?: PerformanceClubRow[] }}
 */
export function HomePerformancePickerModal({ isOpen, onClose, clubs = [] }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const sorted = useMemo(() => {
    const list = (clubs || []).map((c) => ({
      id: c.id,
      name: c.name?.trim() || 'Unknown club',
      location: c.location ?? null,
      club_role: c.club_role || 'member'
    }));
    return sortPerformanceClubs(list);
  }, [clubs]);

  const shortlist = useMemo(() => sorted.slice(0, 5), [sorted]);

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return sorted;
    return sorted.filter((c) => matchesQuery(c, q));
  }, [sorted, query]);

  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  const goMyGames = useCallback(() => {
    onClose();
    navigate('/profile/games');
  }, [navigate, onClose]);

  const goClubPerformance = useCallback(
    (clubId) => {
      onClose();
      navigate(`/clubs/${clubId}?tab=performance`, {
        state: { fromHomepage: true }
      });
    },
    [navigate, onClose]
  );

  const goDiscoverClubs = useCallback(() => {
    onClose();
    navigate('/clubs');
  }, [navigate, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent
        size="medium"
        className="flex min-h-0 flex-col overflow-hidden"
        aria-labelledby="home-performance-picker-title"
      >
        <div className="shrink-0">
          <ModalCloseButton onClose={onClose} />
          <Heading id="home-performance-picker-title" level={3} className="mb-1 pr-10">
            Stats &amp; performance
          </Heading>
          <Text className="text-text-secondary text-sm mb-6">
            Open your cross-club games list or dive into detailed stats for a specific club you belong to.
          </Text>
        </div>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto pr-1">
          <section aria-labelledby="home-performance-general-heading">
            <h4 id="home-performance-general-heading" className={SECTION_LABEL_CLASSES}>
              General performance
            </h4>
            <button
              type="button"
              onClick={goMyGames}
              className="flex w-full items-center gap-3 rounded-xl border border-border-medium bg-white px-4 py-3.5 text-left shadow-brand-sm transition-colors hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 dark:border-border-medium dark:bg-surface dark:hover:bg-surface-subtle"
            >
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-subtle text-lg ring-1 ring-border-light/70 dark:bg-surface-elevated dark:ring-border-medium"
                aria-hidden
              >
                📋
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-text-primary">View my games</span>
                <span className="mt-0.5 block text-xs text-text-secondary leading-snug">
                  Games, results, and history across all clubs.
                </span>
              </span>
            </button>
          </section>

          <section aria-labelledby="home-performance-club-heading">
            <h4 id="home-performance-club-heading" className={SECTION_LABEL_CLASSES}>
              Club performance
            </h4>

            {!sorted.length ? (
              <div className="rounded-xl border border-dashed border-border-medium bg-surface-subtle/60 px-4 py-6 text-center dark:bg-surface-subtle/30">
                <Text className="text-sm text-text-secondary mb-4 block">
                  Join a club to see rankings, streaks, and matchups in that venue.
                </Text>
                <Button type="button" variant="outline" size="small" onClick={goDiscoverClubs}>
                  Discover clubs
                </Button>
              </div>
            ) : (
              <>
                <label htmlFor="home-performance-club-search" className="sr-only">
                  Search my clubs by name or location
                </label>
                <div className="relative mb-3">
                  <input
                    id="home-performance-club-search"
                    type="search"
                    autoComplete="off"
                    placeholder="Search your clubs…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className={SEARCH_INPUT_CLASSES}
                  />
                  <span
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
                    aria-hidden
                  >
                    🔍
                  </span>
                  {query ? (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg px-2 py-1 text-xs text-text-muted hover:bg-surface-subtle hover:text-text-primary"
                      onClick={() => setQuery('')}
                      aria-label="Clear search"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>

                {!query.trim() && shortlist.length > 0 ? (
                  <div className="mb-3">
                    <Text className="text-xs text-text-secondary mb-2 block">Quick picks</Text>
                    <ul
                      role="list"
                      className="divide-y divide-border-medium overflow-hidden rounded-xl border border-border-medium bg-white dark:bg-surface"
                    >
                      {shortlist.map((club) => (
                        <li key={club.id}>
                          <button
                            type="button"
                            onClick={() => goClubPerformance(club.id)}
                            className="flex w-full items-start gap-3 px-3.5 py-3 text-left transition-colors hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/35"
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-text-primary">
                                {club.name}
                              </span>
                              {club.location ? (
                                <span className="mt-0.5 block line-clamp-2 text-xs text-text-secondary leading-snug">
                                  {club.location}
                                </span>
                              ) : null}
                            </span>
                            <span className="shrink-0 pt-0.5 text-xs text-text-muted" aria-hidden>
                              →
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {query.trim() ? (
                  <div>
                    <Text className="text-xs text-text-secondary mb-2 block">
                      {filtered.length === 0
                        ? 'No clubs match that search.'
                        : `${filtered.length} club${filtered.length === 1 ? '' : 's'}`}
                    </Text>
                    {filtered.length > 0 ? (
                      <ul className="flex max-h-[min(40vh,14rem)] flex-col gap-2 overflow-y-auto pr-0.5">
                        {filtered.map((club) => (
                          <li key={club.id}>
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full h-auto min-h-[3rem] justify-start py-2.5 px-3 text-left whitespace-normal"
                              onClick={() => goClubPerformance(club.id)}
                            >
                              <span className="flex min-w-0 flex-col items-start gap-0.5">
                                <span className="w-full truncate font-medium text-text-primary">{club.name}</span>
                                {club.location ? (
                                  <span className="w-full line-clamp-2 text-xs font-normal text-text-secondary">
                                    {club.location}
                                  </span>
                                ) : null}
                              </span>
                            </Button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ) : sorted.length > 5 ? (
                  <Text className="text-xs text-text-secondary">
                    More than five clubs? Use search to find one quickly.
                  </Text>
                ) : null}
              </>
            )}
          </section>
        </div>
      </ModalContent>
    </Modal>
  );
}
